import { useEffect, useRef, useState } from "react";
import type { Route } from "./+types/studio";
import { MarketingLayout } from "~/components/marketing/MarketingLayout";
import { BrilliantCut } from "~/components/marketing/svg/BrilliantCut";
import { GoldMesh } from "~/components/marketing/svg/GoldMesh";
import { detectLocale, translations } from "~/i18n/index";
import { useT } from "~/i18n/context";
import { socialMeta } from "~/lib/seo";

export async function loader({ request }: Route.LoaderArgs) {
  const locale = detectLocale(
    request.headers.get("Cookie"),
    request.headers.get("Accept-Language"),
  );
  return { locale };
}

export function meta({ data }: Route.MetaArgs) {
  const locale = data?.locale ?? "en";
  const t = translations[locale];
  const m = (t as typeof translations["en"]).forStudio?.meta ?? {
    title: "Caratzon Studio · Where AI Meets the Jeweler's Bench",
    description: "The AI-powered design workspace for jewelry creators.",
  };
  return [
    { title: m.title },
    { name: "description", content: m.description },
    ...socialMeta({ title: m.title, description: m.description, pathname: "/studio", locale }),
  ];
}

function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add("visible"); },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

const PIPELINE_ICONS: Record<string, (props: { size?: number; color?: string }) => React.JSX.Element> = {
  pencil: ({ size = 18, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  ),
  "message-circle": ({ size = 18, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
    </svg>
  ),
  image: ({ size = 18, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
    </svg>
  ),
  save: ({ size = 18, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
      <path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7" />
      <path d="M7 3v4a1 1 0 0 0 1 1h7" />
    </svg>
  ),
  "book-open": ({ size = 18, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 7v14" />
      <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3Z" />
    </svg>
  ),
  "trending-up": ({ size = 18, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </svg>
  ),
};

function PipelineIcon({ name, size = 18, color = "currentColor" }: { name: string; size?: number; color?: string }) {
  const Icon = PIPELINE_ICONS[name];
  if (!Icon) return null;
  return <Icon size={size} color={color} />;
}

function CaraConversationDemo({ demo }: { demo: typeof translations["en"]["forStudio"]["cara"]["conversationDemo"] }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (step < 4) {
      const delay = step === 0 ? 2000 : step === 1 ? 3000 : step === 2 ? 2500 : 3500;
      const timer = setTimeout(() => setStep((s) => s + 1), delay);
      return () => clearTimeout(timer);
    }
    const resetTimer = setTimeout(() => setStep(0), 6000);
    return () => clearTimeout(resetTimer);
  }, [step]);

  return (
    <div className="mt-6 border border-border/60 p-5 lg:p-7 relative">
      <div className="absolute top-0 left-6 -translate-y-1/2 px-3 bg-background">
        <span className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground" style={{ fontFamily: "var(--font-sans)" }}>{demo.label}</span>
      </div>

      {/* User uploads sketch */}
      <div className="flex justify-end mb-3">
        <div className="bg-primary/8 rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[85%]">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
              <PipelineIcon name="pencil" size={16} color="var(--muted-foreground)" />
            </div>
            <span className="text-[10px] text-muted-foreground" style={{ fontFamily: "var(--font-sans)" }}>sketch.jpg</span>
          </div>
          <p className="text-sm text-foreground/90 leading-relaxed">{demo.userSketch}</p>
        </div>
      </div>

      {/* Cara analysis + questions */}
      <div className={`flex justify-start mb-3 transition-all duration-500 ${step >= 1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>
        <div className="bg-card rounded-2xl rounded-bl-sm px-4 py-2.5 max-w-[90%] border border-border/40">
          <p className="text-sm text-foreground/90 leading-relaxed">
            <strong className="text-foreground">{demo.caraAnalysis}</strong>
          </p>
          <ul className="mt-2 space-y-1.5 text-sm text-foreground/80">
            {demo.caraQuestions.map((q: string, i: number) => (
              <li key={i} className={`transition-all duration-400 ${step >= 1 ? "opacity-100" : "opacity-0"}`} style={{ transitionDelay: `${i * 200}ms` }}>
                <span style={{ color: "var(--teal-sapphire)" }}>?</span> {q}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* User reply */}
      <div className={`flex justify-end mb-3 transition-all duration-500 ${step >= 2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>
        <div className="bg-primary/8 rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[80%]">
          <p className="text-sm text-foreground/90 leading-relaxed">{demo.userReply}</p>
        </div>
      </div>

      {/* Cara confirms + renders */}
      <div className={`flex justify-start transition-all duration-500 ${step >= 3 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>
        <div className="bg-card rounded-2xl rounded-bl-sm px-4 py-2.5 max-w-[90%] border border-border/40">
          <p className="text-sm text-foreground/90 leading-relaxed italic">{demo.caraConfirm}</p>
          <div className="flex items-center gap-2 mt-2 text-xs" style={{ color: "var(--teal-sapphire)" }}>
            <span className="h-3 w-3 border border-current border-t-transparent rounded-full animate-spin" />
            {demo.toolLabel}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ForStudiosPage() {
  const { t } = useT();
  const h = (t as typeof translations["en"]).forStudio;

  const caraRef = useReveal(0.1);
  const pipelineRef = useReveal(0.08);
  const moreRef = useReveal(0.1);
  const ctaRef = useReveal();

  if (!h) return null;

  return (
    <MarketingLayout>

      {/* ══ HERO ══ */}
      <section className="px-6 lg:px-12 pt-16 pb-16 lg:pt-24 lg:pb-20 relative">
        <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          {Array.from({ length: 10 }).map((_, i) => (
            <line key={i} x1={`${(i + 1) * 9}%`} y1="0" x2={`${(i + 1) * 9}%`} y2="100%" stroke="oklch(0.085 0.004 50)" strokeWidth="1" opacity="0.025" />
          ))}
        </svg>

        <p className="issue-label mb-6 relative" style={{ fontFamily: "var(--font-sans)" }}>{h.hero.overline}</p>

        <h1 className="font-display font-light leading-[0.92] tracking-tight text-foreground max-w-4xl relative" style={{ fontSize: "clamp(3.5rem, 8vw, 7.5rem)" }}>
          {h.hero.h1Line1}
          <br />
          <em className="not-italic" style={{ fontWeight: 300, opacity: 0.42 }}>{h.hero.h1Line2}</em>
        </h1>

        <div className="mt-12 grid lg:grid-cols-[1fr_360px] items-start gap-8 relative">
          <div className="space-y-8">
            <p className="font-display font-light text-muted-foreground" style={{ fontSize: "clamp(1.05rem, 1.8vw, 1.4rem)", lineHeight: 1.5, maxWidth: "44ch" }}>
              {h.hero.tagline}
            </p>
            <div className="flex items-center gap-4">
              <a href="/inspire/creator-onboarding" className="inline-block px-8 py-4 bg-teal text-teal-fg hover:bg-teal-hover transition-colors" style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>
                {h.hero.cta}
              </a>
              <a href="/brands/pricing" className="inline-block issue-label hover:text-foreground transition-colors" style={{ fontFamily: "var(--font-sans)", opacity: 0.45 }}>
                {h.hero.ctaSecondary}
              </a>
            </div>
          </div>
          <div className="hidden lg:block relative overflow-hidden bg-muted ml-auto" style={{ aspectRatio: "3/4", width: "100%", maxWidth: "320px" }}>
            <img src="https://images.unsplash.com/photo-1617038260897-41a1f14a8ca0?auto=format&fit=crop&w=320&q=80" alt="" aria-hidden="true" className="w-full h-full object-cover" style={{ filter: "grayscale(0.10) contrast(1.06) saturate(0.85)" }} loading="eager" />
            <div className="absolute inset-[5px] border border-white/15 pointer-events-none" />
          </div>
        </div>
      </section>

      {/* ── Stat bar ── */}
      <div className="px-6 lg:px-12">
        <div className="rule" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0">
          {([
            [h.stats.aiAssisted, h.stats.designTools],
            [h.stats.stoneTypes, h.stats.stoneLabel],
            [h.stats.phases, h.stats.phasesLabel],
            [h.stats.renders, h.stats.rendersLabel],
          ] as [string, string][]).map(([value, label], i) => (
            <div key={label} className={`py-5 ${i > 0 ? "pl-6 border-l border-border" : ""}`}>
              <p className="font-display font-light text-foreground leading-none mb-1" style={{ fontSize: "clamp(0.9rem, 1.6vw, 1.3rem)" }}>{value}</p>
              <p className="issue-label" style={{ fontFamily: "var(--font-sans)", opacity: 0.45 }}>{label}</p>
            </div>
          ))}
        </div>
        <div className="rule" />
      </div>

      {/* ══ CARA — FROM SKETCH TO SALES ══ */}
      <section id="cara" className="px-6 lg:px-12 py-16 lg:py-24 relative overflow-hidden scroll-mt-20">
        <div className="absolute right-0 top-0 bottom-0 flex items-center pointer-events-none" aria-hidden="true">
          <BrilliantCut size={480} opacity={0.04} animated />
        </div>
        <div ref={caraRef} className="reveal relative">
          <div className="flex items-baseline justify-between mb-2">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{h.cara.overline}</span>
            <span className="issue-label hidden lg:block" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>{h.cara.subtitle}</span>
          </div>
          <h2 className="font-display font-light text-foreground mb-8" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", lineHeight: 1, maxWidth: "24ch" }}>
            {h.cara.heading.split("\n").map((line: string, i: number) => (
              <span key={i}>{i === 0 ? line : <em className="not-italic" style={{ opacity: 0.4 }}>{line}</em>}{i === 0 && <br />}</span>
            ))}
          </h2>

          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-16 items-start">
            <div className="space-y-5">
              <p className="font-display font-light text-foreground leading-snug" style={{ fontSize: "clamp(1.1rem, 1.8vw, 1.4rem)", lineHeight: 1.55 }}>
                {h.cara.p1}
              </p>
              <p className="font-display font-light text-muted-foreground" style={{ fontSize: "clamp(1rem, 1.5vw, 1.2rem)", lineHeight: 1.6 }}>
                {h.cara.p2}
              </p>

              {/* Simulated Cara sketch-to-concept conversation */}
              <CaraConversationDemo demo={h.cara.conversationDemo} />

              <div className="pt-4">
                <a href="/studio/cara" className="inline-block px-6 py-3 bg-teal text-teal-fg hover:bg-teal-hover transition-colors" style={{ fontSize: "0.62rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>
                  {h.cara.cta}
                </a>
              </div>
            </div>

            {/* Six-step pipeline */}
            <div className="space-y-0">
              {h.cara.pipeline.map((step: { num: string; label: string; heading: string; body: string; icon: string }) => (
                <div key={step.label}>
                  <div className="rule" />
                  <div className="py-5">
                    <div className="flex items-start gap-4 mb-1">
                      <div className="shrink-0 flex items-center justify-center mt-0.5" style={{ width: 36, height: 36 }}>
                        <PipelineIcon name={step.icon} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-3 mb-0.5">
                          <span className="font-display font-light select-none" style={{ fontSize: "1.3rem", lineHeight: 1, color: "var(--teal-sapphire)", opacity: 0.5 }}>
                            {step.num}
                          </span>
                          <div>
                            <span className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground/50 block" style={{ fontFamily: "var(--font-sans)" }}>{step.label}</span>
                            <h3 className="font-display font-normal text-foreground" style={{ fontSize: "1rem" }}>{step.heading}</h3>
                          </div>
                        </div>
                        <p className="text-muted-foreground leading-relaxed mt-1" style={{ fontSize: "0.78rem", maxWidth: "36ch" }}>{step.body}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <div className="rule" />
            </div>
          </div>
        </div>
      </section>

      {/* ══ PIPELINE VISUAL — horizontal flow ══ */}
      <section className="py-12 lg:py-16 overflow-hidden relative">
        <GoldMesh opacity={0.06} />
        <div ref={pipelineRef} className="reveal px-6 lg:px-12 relative">
          <div className="text-center mb-10">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>The Complete Journey</span>
          </div>
          <div className="flex items-center justify-between gap-0 overflow-x-auto pb-4 lg:pb-0">
            {h.cara.pipeline.map((step: { num: string; label: string; heading: string; icon: string }, i: number) => (
              <div key={step.num} className="flex items-center shrink-0 lg:shrink">
                <div
                  className="flex flex-col items-center text-center transition-all duration-300"
                  style={{ width: "clamp(100px, 14vw, 140px)" }}
                >
                  <div
                    className="flex items-center justify-center rounded-full border transition-colors mb-3"
                    style={{
                      width: 56, height: 56,
                      borderColor: "var(--teal-sapphire)",
                      background: `oklch(0.45 0.08 213 / 0.06)`,
                    }}
                  >
                    <PipelineIcon name={step.icon} size={22} color="var(--teal-sapphire)" />
                  </div>
                  <span className="font-display font-normal text-foreground block" style={{ fontSize: "0.82rem", lineHeight: 1.2 }}>
                    {step.heading}
                  </span>
                  <span className="text-[10px] tracking-[0.15em] uppercase text-muted-foreground/50 mt-1" style={{ fontFamily: "var(--font-sans)" }}>
                    {step.label}
                  </span>
                </div>
                {i < h.cara.pipeline.length - 1 && (
                  <div className="flex items-center px-1 lg:px-2 shrink-0">
                    <svg width="32" height="8" viewBox="0 0 32 8" fill="none" aria-hidden="true">
                      <line x1="0" y1="4" x2="24" y2="4" stroke="var(--teal-sapphire)" strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
                      <polygon points="24,1 30,4 24,7" fill="var(--teal-sapphire)" opacity="0.4" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ EXPLORE MORE ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-24">
        <div ref={moreRef} className="reveal">
          <span className="issue-label block mb-8" style={{ fontFamily: "var(--font-sans)" }}>Explore the Platform</span>

          <div className="grid lg:grid-cols-2 gap-6">
            <a
              href="/designers"
              className="group block border border-border hover:border-foreground/20 transition-colors p-8 lg:p-10 relative overflow-hidden"
            >
              <div className="absolute right-0 top-0 bottom-0 flex items-center pointer-events-none" aria-hidden="true">
                <BrilliantCut size={200} opacity={0.04} animated />
              </div>
              <span className="issue-label block mb-4" style={{ fontFamily: "var(--font-sans)", color: "var(--teal-sapphire)" }}>For Designers</span>
              <h3 className="font-display font-light text-foreground mb-3" style={{ fontSize: "clamp(1.4rem, 2.5vw, 2rem)", lineHeight: 1.1 }}>
                AI Story Lab, Collections,
                <br />
                <em className="not-italic" style={{ opacity: 0.4 }}>Revenue Models & More</em>
              </h3>
              <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.84rem", maxWidth: "38ch" }}>
                Discover how Caratzon helps designers build their brand, protect IP, and earn perpetual royalties through AI-powered storytelling and a global trade catalog.
              </p>
              <span className="inline-block mt-6 issue-label group-hover:text-foreground transition-colors" style={{ fontFamily: "var(--font-sans)", color: "var(--teal-sapphire)" }}>
                Explore Designer Features →
              </span>
            </a>

            <a
              href="/brands"
              className="group block border border-border hover:border-foreground/20 transition-colors p-8 lg:p-10 relative overflow-hidden"
            >
              <span className="issue-label block mb-4" style={{ fontFamily: "var(--font-sans)", color: "var(--teal-sapphire)" }}>For Brands</span>
              <h3 className="font-display font-light text-foreground mb-3" style={{ fontSize: "clamp(1.4rem, 2.5vw, 2rem)", lineHeight: 1.1 }}>
                Acquisition Models,
                <br />
                <em className="not-italic" style={{ opacity: 0.4 }}>Production & Fulfillment</em>
              </h3>
              <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.84rem", maxWidth: "38ch" }}>
                Three licensing tiers, managed production from our Guangdong facility, and content-ready assets — everything a brand needs to go to market.
              </p>
              <span className="inline-block mt-6 issue-label group-hover:text-foreground transition-colors" style={{ fontFamily: "var(--font-sans)", color: "var(--teal-sapphire)" }}>
                Explore Brand Features →
              </span>
            </a>
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ CTA ══ */}
      <section className="px-6 lg:px-12 py-20 lg:py-28">
        <div ref={ctaRef} className="reveal grid lg:grid-cols-[1fr_auto] items-end gap-8">
          <div>
            <span className="issue-label block mb-5" style={{ fontFamily: "var(--font-sans)" }}>{h.cta.overline}</span>
            <h2 className="font-display font-light leading-[0.92] tracking-tight text-foreground" style={{ fontSize: "clamp(2.8rem, 6vw, 5.5rem)" }}>
              {h.cta.heading.split("\n").map((line: string, i: number) => (
                <span key={i}>{i === 0 ? line : <em className="not-italic" style={{ fontWeight: 300, opacity: 0.45 }}>{line}</em>}{i === 0 && <br />}</span>
              ))}
            </h2>
          </div>
          <div className="pb-2 space-y-4">
            <a href="/inspire/creator-onboarding" className="block px-8 py-4 bg-teal text-teal-fg hover:bg-teal-hover transition-colors text-center" style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>
              {h.cta.primary}
            </a>
            <a href="/trade/catalog" className="block text-center issue-label hover:text-foreground transition-colors" style={{ fontFamily: "var(--font-sans)", opacity: 0.45 }}>
              {h.cta.secondary}
            </a>
          </div>
        </div>
      </section>

    </MarketingLayout>
  );
}
